import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { useApi } from '../services/apiService';
import { Account } from '../types/Account';
import { formatLastUpdated } from '../utils/accountStatus';
import { parseAmount } from '../utils/format';

interface BalanceDialogProps {
  account: Account;
  // 'convert' turns a synced account into a manual one before saving.
  mode: 'update' | 'convert';
  onClose: () => void;
  onSaved: () => void;
}

// Mounted only while open, so each opening starts from the current balance.
const BalanceDialog: React.FC<BalanceDialogProps> = ({
  account,
  mode,
  onClose,
  onSaved,
}) => {
  const { convertToManualAccount, updateManualBalance } = useApi();
  const [balanceInput, setBalanceInput] = useState(
    Number(account.balance.current).toFixed(2),
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balance = parseAmount(balanceInput);
  const converting = mode === 'convert';

  const handleConfirm = async () => {
    setSubmitted(true);
    if (balance === null) return;
    setSaving(true);
    setError(null);
    try {
      // Both calls are idempotent, so a retry after a partial failure is safe.
      if (converting) await convertToManualAccount(account._id);
      await updateManualBalance(account._id, balance);
      onSaved();
    } catch (err) {
      setError('Could not save the balance. Please try again.');
      setSaving(false);
    }
  };

  return (
    <ConfirmDialog
      open
      title={converting ? 'Convert to manual account?' : 'Update balance'}
      confirmLabel={converting ? 'Convert account' : 'Save balance'}
      busy={saving}
      onConfirm={handleConfirm}
      onCancel={onClose}
    >
      <p>
        <strong>{account.name}</strong>
        {account.connection.name ? ` · ${account.connection.name}` : ''}
      </p>
      {converting && (
        <p className="muted">
          Akahu will stop updating this account and you'll enter its balance
          yourself. Its history is kept. This can't be switched back.
        </p>
      )}
      <label className="field">
        <span className="field-label">
          Current balance ({account.balance.currency})
        </span>
        <input
          className="input num"
          value={balanceInput}
          onChange={(event) => setBalanceInput(event.target.value)}
          onFocus={(event) => event.target.select()}
          inputMode="decimal"
          aria-invalid={submitted && balance === null}
        />
        <span className="field-hint">
          {formatLastUpdated(account.balance.recordDate)}. Use a negative amount
          for loans and credit cards.
        </span>
      </label>
      {error && <p className="error">{error}</p>}
    </ConfirmDialog>
  );
};

export default BalanceDialog;
